import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import logo from './Images/logo.jpg';
import Hamburger from './Hamburger.js';
import productsData from '../../data/productsData';

const Header = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [mobileMenu, setMobileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);

  // Reset search and menu on route change
  useEffect(() => {
    setSearchTerm('');
    setShowSuggestions(false);
    setMobileMenu(false);
  }, [location.pathname]);

  // Filter suggestions from static data
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const filtered = productsData.filter((p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  }, [searchTerm]);

  // Close suggestions on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchTerm.trim();

    if (trimmed === '') {
      setPopupMessage('Please enter a product name');
      setShowPopup(true);
      return;
    }

    const match = productsData.find(
      (p) => p.name.toLowerCase().includes(trimmed.toLowerCase())
    );

    if (match) {
      setSearchTerm('');
      setShowSuggestions(false);
      setMobileMenu(false);
      navigate(`/products/${match.id}`);
    } else {
      setPopupMessage(`"${searchTerm}" not found. Try a valid product name.`);
      setShowPopup(true);
    }
  };

  const handleSuggestionClick = (product) => {
    setSearchTerm('');
    setShowSuggestions(false);
    setMobileMenu(false);
    navigate(`/products/${product.id}`);
  };

  const handleHamburger = () => setMobileMenu((prev) => !prev);
  const closeMenu = () => setMobileMenu(false);

  return (
    <div>
      <nav className="navbar">
        <div className="logo">
          <img src={logo} alt="Karim Industries Logo" />
          <span>
            Karim <br />Industries
          </span>
        </div>

        <Hamburger isActive={mobileMenu} onClick={handleHamburger} />

        <ul className={`nav-links${mobileMenu ? ' active' : ''}`}>
          <li>
            <Link to="/" onClick={closeMenu}>Home</Link>
          </li>
          <li>
            <Link to="/AboutUs" onClick={closeMenu}>About Us</Link>
          </li>
          <li className="dropdown">
            <Link to="/Services">Services</Link>
            <ul className="dropdown-menu">
              {window.innerWidth <= 768 ? (
                <>
                  <li className="left-links">
                    <Link to="/DistributerCollaboration">Distributor Collaborations</Link>
                    <Link to="/LogisticManagement">Logistics Management</Link>
                    <Link to="/StandardCompliance">Standards Compliance</Link>
                    <Link to="/ExhibitionProgram">Exhibition Programs</Link>
                    <Link to="/MarketCompliance">Market Compliance</Link>
                    <Link to="/GlobalExport">Global Export</Link>
                  </li>
                </>
              ) : (
                <>
                  <li className="dropdown-submenu">
                    <Link to="/StandardCompliance" onClick={closeMenu}>Standards Compliance</Link>
                    <ul className="dropdown-submenu">
                      <li>
                        <Link to="/MarketCompliance" onClick={closeMenu}>Market Compliance</Link>
                      </li>
                    </ul>
                  </li>
                  <li className="dropdown-submenu">
                    <Link to="/GlobalExport" onClick={closeMenu}>Global Export</Link>
                    <ul className="dropdown-submenu">
                      <li>
                        <Link to="/LogisticManagement" onClick={closeMenu}>Logistics Management</Link>
                      </li>
                    </ul>
                  </li>
                  <li className="dropdown-submenu">
                    <Link to="/ExhibitionProgram" onClick={closeMenu}>Exhibition Programs</Link>
                    <ul className="dropdown-submenu">
                      <li>
                        <Link to="/DistributerCollaboration" onClick={closeMenu}>Distributor Collaborations</Link>
                      </li>
                    </ul>
                  </li>
                </>
              )}
            </ul>
          </li>
          <li>
            <Link to="/Products" onClick={closeMenu}>Products</Link>
          </li>
          <li>
            <Link to="/ContactUs" onClick={closeMenu}>Contact Us</Link>
          </li>
          {/* Catalogue download — mobile nav only */}
          <li className="nav-catalogue-item">
            <a
              href="https://drive.google.com/uc?export=download&id=1buzNLVYSZ3GA-3Ne3eWWmlFbIyqay8kH"
              download="Karim Industries Catalogue.pdf"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
              className="nav-catalogue-link"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v13M7 11l5 5 5-5" />
                <path d="M4 20h16" />
              </svg>
              Download Catalogue
            </a>
          </li>
        </ul>

        <div className="search-bar" ref={searchInputRef}>
          <form onSubmit={handleSearch} autoComplete="off">
            <input
              type="text"
              placeholder="Search by name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => {
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
            />
            <button type="submit">Search</button>
          </form>

          {showSuggestions && suggestions.length > 0 && (
            <ul className="search-suggestions">
              {suggestions.map((product) => (
                <li key={product.id} onClick={() => handleSuggestionClick(product)}>
                  {product.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        {showPopup && (
          <div className="popup-overlay">
            <div className="popup-box">
              <p>{popupMessage}</p>
              <button onClick={() => setShowPopup(false)}>OK</button>
            </div>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Header;
